import { CartLinesUpdateEvent } from '@shopify/events';

(() => {
  const TAG = 'teaballs-cart-merchandising';
  if (customElements.get(TAG)) return;

  const asNumber = (value, fallback = 0) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  };

  const root = () => window.Shopify?.routes?.root || '/';

  class TeaballsCartMerchandising extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      this.recommendationsLoaded = false;
      this.cartSignature = this.cartSignatureValue();
      this.renderBase();

      const signal = this.abort.signal;
      document.addEventListener('teaballs:promotion:ready', () => this.resetAndReload(), { signal });
      document.addEventListener('teaballs:promotion:change', () => this.resetAndReload(), { signal });

      this.cartObserver?.disconnect();
      this.cartObserver = new MutationObserver(() => {
        const nextSignature = this.cartSignatureValue();
        if (!nextSignature || nextSignature === this.cartSignature) return;
        this.cartSignature = nextSignature;
        this.resetAndReload();
      });
      this.cartObserver.observe(this, { childList: true, characterData: true, subtree: true });

      requestAnimationFrame(() => this.loadRecommendations());
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.cartObserver?.disconnect();
    }

    cartData() {
      const node = this.querySelector('[data-tb-cart-data]');
      if (!node) return null;
      try {
        return JSON.parse(node.textContent || '{}');
      } catch {
        return null;
      }
    }

    cartSignatureValue() {
      return this.querySelector('[data-tb-cart-data]')?.textContent || '';
    }

    resetAndReload() {
      this.recommendationsLoaded = false;
      this.querySelectorAll('.tb-cart-addon--recommendation').forEach((node) => node.remove());
      this.renderBase();
      requestAnimationFrame(() => this.loadRecommendations());
    }

    promotion() {
      return window.TeaballsPromotion || null;
    }

    promotionState() {
      return this.promotion()?.current?.() || null;
    }

    eligibleCollection() {
      const promo = this.promotion();
      const state = promo?.current?.();
      if (state?.eligibleCollection?.productIds?.length) return state.eligibleCollection;
      return promo?.raw?.()?.evergreen?.eligibleCollection || null;
    }

    eligibleIds() {
      return new Set((this.eligibleCollection()?.productIds || []).map(String));
    }

    eligibleQuantity(cart) {
      const ids = this.eligibleIds();
      return (cart?.items || []).reduce((sum, item) => {
        if (!ids.has(String(item.product_id))) return sum;
        return sum + asNumber(item.quantity, 0);
      }, 0);
    }

    observedEligibleUnitPrice(cart, basePriceCents) {
      const ids = this.eligibleIds();
      for (const item of cart?.items || []) {
        if (!ids.has(String(item.product_id))) continue;
        const quantity = Math.max(1, asNumber(item.quantity, 1));
        const originalLine = asNumber(item.original_line_price ?? item.line_price ?? item.final_line_price, 0);
        const finalLine = asNumber(item.final_line_price ?? item.line_price ?? originalLine, originalLine);
        if (!originalLine || finalLine >= originalLine) continue;
        const originalUnit = Math.round(originalLine / quantity);
        const finalUnit = Math.round(finalLine / quantity);
        if (Math.abs(originalUnit - basePriceCents) <= 1 && finalUnit < originalUnit) return finalUnit;
      }
      return null;
    }

    projectedCandidatePrice(candidate, cart) {
      const regularCents = asNumber(candidate?.priceCents, 0);
      const bf=window.TeaballsBFPricing;
      if(bf?.active()) {const finalCents=bf.price(candidate.variantId,regularCents);return {regularCents,finalCents,discountPercent:bf.rate(candidate.variantId),discounted:finalCents<regularCents};}
      if ((document.documentElement.dataset.tbIdentitySurfaces || '').split(' ').includes('cart')) return { regularCents, finalCents: regularCents, discountPercent: 0, discounted: false };
      const promo = this.promotion();
      const state = promo?.current?.();
      if (!regularCents || !promo?.metricsFromCart || state?.status !== 'active') {
        return { regularCents, finalCents: regularCents, discountPercent: 0, discounted: false };
      }

      if (!this.eligibleIds().has(String(candidate.productId))) {
        return { regularCents, finalCents: regularCents, discountPercent: 0, discounted: false };
      }

      const currentMetrics = promo.metricsFromCart(cart);
      const projectedCart = {
        ...cart,
        items: [
          ...(cart?.items || []),
          {
            product_id: candidate.productId,
            variant_id: candidate.variantId,
            quantity: 1,
            original_line_price: regularCents,
            line_price: regularCents,
            final_line_price: regularCents,
          },
        ],
        items_subtotal_price: asNumber(cart?.items_subtotal_price, 0) + regularCents,
        original_total_price: asNumber(cart?.original_total_price, 0) + regularCents,
        total_price: asNumber(cart?.total_price, 0) + regularCents,
      };
      const projectedMetrics = promo.metricsFromCart(projectedCart);
      const discountPercent = asNumber(projectedMetrics?.discountPercent, 0);
      if (discountPercent <= 0) {
        return { regularCents, finalCents: regularCents, discountPercent: 0, discounted: false };
      }

      let finalCents = regularCents - Math.floor(regularCents * discountPercent / 100);

      // When the projected tier is already active, mirror the actual Shopify line price.
      // This preserves any presentment-market rounding Shopify has already applied.
      if (asNumber(currentMetrics?.discountPercent, 0) === discountPercent) {
        const observed = this.observedEligibleUnitPrice(cart, regularCents);
        if (observed !== null) finalCents = observed;
      }

      finalCents = Math.max(0, Math.min(regularCents, finalCents));
      return {
        regularCents,
        finalCents,
        discountPercent,
        discounted: finalCents < regularCents,
      };
    }

    standState(cart) {
      const eligibleQuantity = this.eligibleQuantity(cart);
      const three = (cart?.items || []).find((item) => String(item.product_id) === '6994225201303');
      const five = (cart?.items || []).find((item) => String(item.product_id) === '6628940906647');

      if (eligibleQuantity < 3 || five) return { eligibleQuantity, template: null, mode: null, removeKey: null };
      if (three && eligibleQuantity >= 4) return { eligibleQuantity, template: '5', mode: 'upgrade', removeKey: three.key };
      if (three) return { eligibleQuantity, template: null, mode: null, removeKey: null };
      if (eligibleQuantity === 3) return { eligibleQuantity, template: '3', mode: 'add', removeKey: null };
      return { eligibleQuantity, template: '5', mode: 'add', removeKey: null };
    }

    buildStandCard(state) {
      if (!state.template) return null;
      const template = this.querySelector(`template[data-tb-stand-template="${state.template}"]`);
      const article = template?.content?.firstElementChild?.cloneNode(true);
      if (!article) return null;

      article.dataset.action = state.mode || 'add';
      if (state.removeKey) article.dataset.removeKey = state.removeKey;

      if (state.mode === 'upgrade') {
        const eyebrow = article.querySelector('[data-tb-stand-eyebrow]');
        const label = article.querySelector('[data-tb-button-label]');
        if (eyebrow) eyebrow.textContent = this.dataset.locale === 'de' ? 'Mehr Platz für deinen Mix' : 'More room for your mix';
        if (label) label.textContent = this.dataset.upgradeLabel || 'Upgrade';
      }

      return article;
    }

    renderCampaignCopy() {
      const state = this.promotionState();
      const node = this.querySelector('[data-tb-campaign-copy]');
      if (!node) return;
      const copy = state?.status === 'active' && state?.surfaces?.cart ? String(state?.copy?.cart || '').trim() : '';
      node.textContent = copy;
      node.hidden = !copy;
    }

    renderBase() {
      const cart = this.cartData();
      if (!cart?.items?.length) {
        this.hidden = true;
        return;
      }

      this.renderCampaignCopy();
      const rail = this.querySelector('[data-tb-merch-rail]');
      if (!rail) return;

      const existingRecommendations = [...rail.querySelectorAll('.tb-cart-addon--recommendation')];
      rail.replaceChildren();

      const stand = window.TeaballsBFPricing?.active() ? null : this.buildStandCard(this.standState(cart));
      if (stand) rail.append(stand);
      existingRecommendations.forEach((card) => rail.append(card));

      this.decorateHeading(cart, Boolean(stand));
      this.wireButtons();
      this.hidden = rail.children.length === 0;
    }

    decorateHeading(cart, hasStand) {
      const heading = this.querySelector('[data-tb-merch-heading]');
      const sub = this.querySelector('[data-tb-merch-sub]');
      const promo = this.promotion();
      const state = promo?.current?.();
      const metrics = promo?.metricsFromCart?.(cart);
      const next = metrics?.tier?.next;
      const remaining = asNumber(metrics?.tier?.remaining, 0);

      if (heading) heading.textContent = (next && remaining > 0 && remaining <= 2)
        ? (this.dataset.headingBuild || this.dataset.headingDefault)
        : (this.dataset.headingDefault || 'Recommended');

      if (!sub) return;
      if(window.TeaballsBFPricing?.active()) {
        if(heading)heading.textContent=window.TeaballsBFPricing.text('gift');
        sub.textContent=window.TeaballsBFPricing.text('giftSub');sub.hidden=false;return;
      }
      let copy = '';
      if (state?.status === 'active' && state?.offer?.type === 'tiered' && next && remaining > 0) {
        copy = this.dataset.locale === 'de'
          ? `Noch ${remaining} ${remaining === 1 ? 'Flasche' : 'Flaschen'} bis ${asNumber(next.discountPercent, 0)} % Mix-Rabatt.`
          : `Add ${remaining} more ${remaining === 1 ? 'bottle' : 'bottles'} to unlock ${asNumber(next.discountPercent, 0)}% Mix discount.`;
      } else if (hasStand) {
        copy = this.dataset.locale === 'de' ? 'Praktisch ergänzen oder noch eine Sorte entdecken.' : 'Add a useful extra or discover another flavour.';
      }
      sub.textContent = copy;
      sub.hidden = !copy;
    }

    recommendationSeeds(cart) {
      const eligible = this.eligibleIds();
      const items = [...(cart?.items || [])].filter((item) => !['6994225201303', '6628940906647'].includes(String(item.product_id)));
      items.sort((a, b) => Number(eligible.has(String(b.product_id))) - Number(eligible.has(String(a.product_id))));
      return [...new Set(items.map((item) => String(item.product_id)))].slice(0, 3);
    }

    async fetchSeed(seedId) {
      const params = new URLSearchParams({
        section_id: this.dataset.recommendationSection || 'cart-smart-recommendations',
        product_id: seedId,
        limit: '6',
        intent: 'related',
      });

      const previewThemeId = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (previewThemeId) params.set('preview_theme_id', previewThemeId);

      const response = await fetch(`${root()}recommendations/products?${params.toString()}`, {
        credentials: 'same-origin',
        signal: this.abort?.signal,
      });
      if (!response.ok) throw new Error(`Recommendation request failed: ${response.status}`);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return [...doc.querySelectorAll('.tb-recommendation-candidate')].map((node) => ({
        productId: String(node.dataset.productId || ''),
        variantId: String(node.dataset.variantId || ''),
        title: node.dataset.title || '',
        price: node.dataset.price || '',
        priceCents: asNumber(node.dataset.priceCents, 0),
        compareCents: asNumber(node.dataset.compareCents, 0),
        url: node.dataset.url || '#',
        image: node.dataset.image || '',
        imageAlt: node.dataset.imageAlt || node.dataset.title || '',
        vendor: node.dataset.vendor || '',
        productType: node.dataset.productType || '',
        position: asNumber(node.dataset.position, 0),
        kind: /set|bundle/i.test(node.dataset.productType || '') ? 'set' : 'native',
        source: 'shopify',
      })).filter((candidate) => candidate.productId && candidate.variantId);
    }

    fallbackCandidates() {
      return [...this.querySelectorAll('[data-tb-fallback-candidate]')].map((node) => ({
        productId: String(node.dataset.productId || ''),
        variantId: String(node.dataset.variantId || ''),
        title: node.dataset.title || '',
        price: node.dataset.price || '',
        priceCents: asNumber(node.dataset.priceCents, 0),
        compareCents: asNumber(node.dataset.compareCents, 0),
        url: node.dataset.url || '#',
        image: node.dataset.image || '',
        imageAlt: node.dataset.imageAlt || node.dataset.title || '',
        vendor: node.dataset.vendor || '',
        productType: node.dataset.productType || '',
        position: asNumber(node.dataset.position, 0),
        kind: node.dataset.kind || 'fallback',
        source: 'fallback',
      })).filter((candidate) => candidate.productId && candidate.variantId);
    }

    rankRecommendations(seedResults, cart) {
      const inCart = new Set((cart?.items || []).map((item) => String(item.product_id)));
      if(window.TeaballsBFPricing?.active()) {
        const pool=[...this.fallbackCandidates(),...seedResults.flat()].filter(c=>!inCart.has(c.productId));
        const unique=pool.filter((c,i,list)=>list.findIndex(x=>x.productId===c.productId)===i);
        const sets=unique.filter(c=>c.kind==='set').sort((a,b)=>a.position-b.position);
        const calendars=unique.filter(c=>c.kind==='calendar');
        const flavours=unique.filter(c=>c.kind!=='set'&&c.kind!=='calendar'&&window.TeaballsBFPricing.rate(c.variantId)>0);
        const hasSet=(cart?.items||[]).some(i=>/set|trio/i.test(i.title||''));
        const chosen=[];
        const threshold=Number(document.querySelector('teaballs-cart-progress')?.dataset.shippingThreshold)||0;
        const remaining=threshold-Number(cart?.total_price||0);
        const topup=remaining>0?flavours.find(c=>window.TeaballsBFPricing.price(c.variantId,c.priceCents)>=remaining):null;
        if(topup)chosen.push(topup);
        if(!hasSet&&sets[0])chosen.push(sets[0]);
        if(calendars[0])chosen.push(calendars[0]);
        if(flavours[0]&&!chosen.some(c=>c.productId===flavours[0].productId))chosen.push(flavours[0]);
        for(const c of [...sets,...flavours])if(chosen.length<3&&!chosen.some(x=>x.productId===c.productId))chosen.push(c);
        return chosen.slice(0,3);
      }
      const eligible = this.eligibleIds();
      const promo = this.promotion();
      const metrics = promo?.metricsFromCart?.(cart);
      const remaining = asNumber(metrics?.tier?.remaining, 0);
      const nextTier = metrics?.tier?.next;
      const tierActive = promo?.current?.()?.offer?.type === 'tiered' && Boolean(nextTier);
      const nearTier = tierActive && remaining > 0 && remaining <= 2;
      const titles = (cart?.items || []).map((item) => String(item.title || ''));
      const bioOnly = titles.length > 0 && titles.some((title) => /\bBIO\b/i.test(title)) && !titles.some((title) => !/\bBIO\b/i.test(title) && /TEABALLS/i.test(title));
      const classicOnly = titles.length > 0 && !titles.some((title) => /\bBIO\b/i.test(title));
      const map = new Map();

      const accept = (candidate) => {
        if (!candidate?.productId || !candidate?.variantId) return false;
        if (inCart.has(candidate.productId)) return false;
        if (['6994225201303', '6628940906647'].includes(candidate.productId)) return false;
        if (/holzaufsteller/i.test(candidate.productType || '') || /holzaufsteller/i.test(candidate.title || '')) return false;
        return true;
      };

      seedResults.forEach((candidates) => {
        candidates.forEach((candidate) => {
          if (!accept(candidate)) return;
          const item = map.get(candidate.productId) || { ...candidate, appearances: 0, score: 0 };
          item.appearances += 1;
          item.score += 120 + Math.max(0, 8 - candidate.position);
          if (eligible.has(candidate.productId)) item.score += nearTier ? 55 : 16;
          map.set(candidate.productId, item);
        });
      });

      for (const candidate of this.fallbackCandidates()) {
        if (!accept(candidate) || map.has(candidate.productId)) continue;
        let score = candidate.kind === 'set' ? 44 : 58;
        if (nearTier && eligible.has(candidate.productId)) score += 85;
        if (nearTier && candidate.kind === 'set') score -= 32;
        if (bioOnly && candidate.kind === 'bio') score += 24;
        if (bioOnly && candidate.kind === 'classic') score -= 12;
        if (classicOnly && candidate.kind === 'classic') score += 24;
        if (classicOnly && candidate.kind === 'bio') score -= 12;
        score += Math.max(0, 12 - candidate.position);
        map.set(candidate.productId, { ...candidate, score, appearances: 0 });
      }

      const ranked = [...map.values()].sort((a, b) => b.score - a.score);
      const eligibleQuantity = asNumber(metrics?.eligibleQuantity, 0);
      const buildingFirstMix = eligibleQuantity > 0 && eligibleQuantity < 5;

      if (nearTier || buildingFirstMix) {
        const chosen = [];
        for (const candidate of ranked) {
          if (chosen.length >= 3) break;
          if (eligible.has(candidate.productId)) chosen.push(candidate);
        }
        for (const candidate of ranked) {
          if (chosen.length >= 3) break;
          if (!chosen.some((item) => item.productId === candidate.productId)) chosen.push(candidate);
        }
        return chosen.slice(0, 3);
      }

      const chosen = [];
      for (const candidate of ranked) {
        if (chosen.length >= 2) break;
        if (candidate.kind !== 'set') chosen.push(candidate);
      }
      const bestSet = ranked.find((candidate) => candidate.kind === 'set' && !chosen.some((item) => item.productId === candidate.productId));
      if (bestSet) chosen.push(bestSet);
      for (const candidate of ranked) {
        if (chosen.length >= 3) break;
        if (!chosen.some((item) => item.productId === candidate.productId)) chosen.push(candidate);
      }
      return chosen.slice(0, 3);
    }

    recommendationCard(candidate, cart) {
      const eligible = this.eligibleIds();
      const promo = this.promotion();
      const metrics = promo?.metricsFromCart?.(cart);
      const tierActive = promo?.current?.()?.offer?.type === 'tiered' && Boolean(metrics?.tier?.next);
      const article = document.createElement('article');
      article.className = 'tb-cart-addon tb-cart-addon--recommendation';
      article.dataset.productId = candidate.productId;
      article.dataset.variantId = candidate.variantId;
      article.dataset.action = 'add';

      const media = document.createElement('a');
      media.className = 'tb-cart-addon__media';
      media.href = candidate.url;
      media.setAttribute('aria-label', candidate.title);
      if (candidate.image) {
        const img = document.createElement('img');
        img.src = candidate.image;
        img.alt = candidate.imageAlt;
        img.loading = 'lazy';
        media.append(img);
      }

      const body = document.createElement('div');
      body.className = 'tb-cart-addon__body';
      const title = document.createElement('a');
      title.className = 'tb-cart-addon__title';
      title.href = candidate.url;
      let displayTitle = candidate.title
        .replace(/^TEABALLS\s+BIO\s*[-–]\s*/i, 'BIO – ')
        .replace(/^TEABALLS\s*[-–]\s*/i, '');
      if (candidate.kind === 'set') {
        displayTitle = displayTitle.split(/\s[-–]\s/)[0];
      }
      title.textContent = displayTitle;

      const price = document.createElement('div');
      price.className = 'tb-cart-addon__price';
      const projected = this.projectedCandidatePrice(candidate, cart);
      if (projected.discounted) {
        const original = document.createElement('s');
        original.className = 'tb-cart-addon__price-original';
        original.textContent = window.TeaballsBFPricing?.active() ? window.TeaballsBFPricing.money(window.TeaballsBFPricing.regular ? window.TeaballsBFPricing.regular(candidate.variantId,candidate.priceCents,candidate.compareCents) : window.TeaballsBFPricing.reference(candidate.variantId,candidate.priceCents)) : candidate.price;
        const finalPrice = document.createElement('strong');
        finalPrice.className = 'tb-cart-addon__price-final';
        finalPrice.textContent = promo?.formatMoney
          ? promo.formatMoney(projected.finalCents)
          : candidate.price;
        price.append(original, finalPrice);
        const ref=window.TeaballsBFPricing?.comparison(candidate.variantId,candidate.priceCents);if(ref){const basis=document.createElement('small');basis.className='tb-bm-comparison-basis';basis.textContent=window.TeaballsBFPricing.savingPercent(candidate.variantId,candidate.priceCents)+' % gegenüber Einzelkauf';price.append(basis);}
      } else if (candidate.compareCents > candidate.priceCents) {
        /* no campaign price: keep the catalogue strike price, as on product cards and product pages */
        const format = (cents) => (promo?.formatMoney ? promo.formatMoney(cents) : window.TeaballsBFPricing?.money ? window.TeaballsBFPricing.money(cents) : new Intl.NumberFormat(document.documentElement.lang || 'de-DE', { style: 'currency', currency: window.Shopify?.currency?.active || 'EUR' }).format(cents / 100));
        const original = document.createElement('s');
        original.className = 'tb-cart-addon__price-original';
        original.textContent = format(candidate.compareCents);
        const finalPrice = document.createElement('strong');
        finalPrice.className = 'tb-cart-addon__price-final';
        finalPrice.textContent = format(candidate.priceCents);
        price.append(original, finalPrice);
      } else {
        price.textContent = candidate.price;
      }

      if(window.TeaballsBFPricing?.active()&&(candidate.kind==='set'||candidate.kind==='calendar')) {
        const eyebrow=document.createElement('div');eyebrow.className='tb-cart-addon__eyebrow';eyebrow.textContent=window.TeaballsBFPricing.text(candidate.kind==='set'?'giftItem':'advent');body.append(eyebrow);
      }
      body.append(title, price);

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'tb-cart-addon__add';
      button.dataset.tbAddButton = '';
      button.setAttribute('aria-label', `${this.dataset.addLabel || 'Add'} ${displayTitle}`);
      button.innerHTML = `<span aria-hidden="true">+</span><span data-tb-button-label>${this.dataset.addLabel || 'Add'}</span>`;

      article.append(media, body, button);
      return article;
    }

    async loadRecommendations() {
      if (this.recommendationsLoaded) return;
      this.recommendationsLoaded = true;
      const cart = this.cartData();
      const seeds = this.recommendationSeeds(cart);

      try {
        const results = seeds.length && !window.TeaballsBFPricing?.active()
          ? await Promise.all(seeds.map((seed) => this.fetchSeed(seed).catch(() => [])))
          : [];
        const ranked = this.rankRecommendations(results, cart);
        const rail = this.querySelector('[data-tb-merch-rail]');
        if (!rail) return;
        rail.querySelectorAll('.tb-cart-addon--recommendation').forEach((node) => node.remove());
        ranked.forEach((candidate) => rail.append(this.recommendationCard(candidate, cart)));
        this.decorateHeading(cart, Boolean(rail.querySelector('.tb-cart-addon--stand')));
        this.wireButtons();
        this.hidden = rail.children.length === 0;
      } catch (error) {
        if (error?.name !== 'AbortError') console.warn('[TEABALLS cart merchandising] Recommendations unavailable.', error);
        const rail = this.querySelector('[data-tb-merch-rail]');
        const fallback = this.rankRecommendations([], cart);
        rail?.querySelectorAll('.tb-cart-addon--recommendation').forEach((node) => node.remove());
        fallback.forEach((candidate) => rail?.append(this.recommendationCard(candidate, cart)));
        this.wireButtons();
        this.hidden = !rail?.children?.length;
      }
    }

    wireButtons() {
      this.querySelectorAll('[data-tb-add-button]').forEach((button) => {
        if (button.dataset.tbBound === 'true') return;
        button.dataset.tbBound = 'true';
        button.addEventListener('click', (event) => this.handleAdd(event));
      });
    }

    setButtonState(button, state) {
      const label = button?.querySelector('[data-tb-button-label]');
      if (!button || !label) return;
      if (state === 'loading') {
        button.disabled = true;
        label.textContent = '…';
      } else if (state === 'added') {
        button.disabled = true;
        label.textContent = this.dataset.addedLabel || 'Added';
      } else {
        button.disabled = false;
        label.textContent = this.dataset.addLabel || 'Add';
      }
    }

    async handleAdd(event) {
      const button = event.currentTarget;
      const card = button.closest('.tb-cart-addon');
      if (!card) return;
      const variantId = asNumber(card.dataset.variantId, 0);
      if (!variantId) return;

      this.setButtonState(button, 'loading');
      const status = this.querySelector('[data-tb-merch-status]');
      if (status) status.textContent = '';

      const sectionIds = [...new Set(
        [...document.querySelectorAll('cart-items-component')]
          .map((item) => item instanceof HTMLElement ? item.dataset.sectionId : '')
          .filter(Boolean)
      )];
      const deferred = CartLinesUpdateEvent.createPromise();

      this.dispatchEvent(new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      }));

      try {
        let mutation = await this.addVariant(variantId, sectionIds);
        if (card.dataset.action === 'upgrade' && card.dataset.removeKey) {
          mutation = await this.removeLine(card.dataset.removeKey, sectionIds);
        }

        const cart = await this.fetchCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            sections: mutation?.sections || undefined,
            items: cart.items,
            itemCount: cart.item_count,
            source: 'teaballs-cart-merchandising',
            productId: card.dataset.productId || '',
            didError: false,
          },
        });

        this.setButtonState(button, 'added');
        this.dispatchEvent(new CustomEvent('teaballs:cart-merch:add', {
          bubbles: true,
          detail: {
            productId: card.dataset.productId || '',
            variantId,
            action: card.dataset.action || 'add',
          },
        }));
      } catch (error) {
        deferred.reject(error);
        console.warn('[TEABALLS cart merchandising] Add-on failed.', error);
        this.setButtonState(button, 'idle');
        if (status) status.textContent = this.dataset.errorLabel || 'Please try again';
      }
    }

    async addVariant(variantId, sectionIds = []) {
      const response = await fetch(`${root()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const payload = await response.json();
      if (!response.ok || payload?.status) throw new Error(payload?.description || payload?.message || `Cart add failed: ${response.status}`);
      return payload;
    }

    async removeLine(key, sectionIds = []) {
      const response = await fetch(`${root()}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          id: key,
          quantity: 0,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const payload = await response.json();
      if (!response.ok || payload?.errors) throw new Error(payload?.errors || `Cart change failed: ${response.status}`);
      return payload;
    }

    async fetchCart() {
      const response = await fetch(`${root()}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`Cart fetch failed: ${response.status}`);
      return response.json();
    }
  }

  customElements.define(TAG, TeaballsCartMerchandising);
})();
